import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { PageHeader, FullPageSpinner, PrimaryButton } from "@/components/kit";
import { Field, inputClass } from "@/components/AuthShell";

export default function VetOnboarding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", city: "", phone: "", hours: "Mon-Fri 9am-6pm", services: "", is_emergency_24_7: false });

  useEffect(() => {
    apiFetch("/vets/mine")
      .then((v) => {
        if (v) setForm({ name: v.name || "", address: v.address || "", city: v.city || "", phone: v.phone || "", hours: v.hours || "Mon-Fri 9am-6pm", services: (v.services || []).join(", "), is_emergency_24_7: !!v.is_emergency_24_7 });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch("/vets/mine", {
        method: "POST",
        body: JSON.stringify({
          name: form.name.trim(),
          address: form.address.trim(),
          city: form.city.trim(),
          phone: form.phone.trim(),
          hours: form.hours.trim(),
          services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
          is_emergency_24_7: form.is_emergency_24_7,
        }),
      });
      toast.success("Clinic listing saved!");
      navigate("/app/vet-dashboard", { replace: true });
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <FullPageSpinner />;

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader title="Your clinic" subtitle="Set up your listing so pet parents can find you." />
      <form onSubmit={save}>
        <Field label="Clinic name">
          <input data-testid="vet-name-input" required value={form.name} onChange={set("name")} placeholder="e.g. Riverside Animal Hospital" className={inputClass} />
        </Field>
        <Field label="Address">
          <input data-testid="vet-address-input" required value={form.address} onChange={set("address")} placeholder="123 Main St" className={inputClass} />
        </Field>
        <Field label="City">
          <input data-testid="vet-city-input" required value={form.city} onChange={set("city")} placeholder="Portland, OR" className={inputClass} />
        </Field>
        <Field label="Phone">
          <input data-testid="vet-phone-input" required value={form.phone} onChange={set("phone")} placeholder="+1-503-555-0100" className={inputClass} />
        </Field>
        <Field label="Hours">
          <input data-testid="vet-hours-input" value={form.hours} onChange={set("hours")} className={inputClass} />
        </Field>
        <Field label="Services (comma separated)">
          <input data-testid="vet-services-input" value={form.services} onChange={set("services")} placeholder="General, Surgery, Dental" className={inputClass} />
        </Field>
        <label className="flex items-center gap-3 my-4 cursor-pointer">
          <input data-testid="vet-emergency-checkbox" type="checkbox" checked={form.is_emergency_24_7} onChange={(e) => setForm((f) => ({ ...f, is_emergency_24_7: e.target.checked }))} className="h-5 w-5 accent-[#E86A45]" />
          <span className="font-bold text-[#2D2B2A]">We offer 24/7 emergency care</span>
        </label>
        <PrimaryButton data-testid="vet-save-btn" type="submit" loading={saving} className="w-full">Save listing</PrimaryButton>
      </form>
    </div>
  );
}
